import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setPage,
  setMaxPages,
  setSorting,
  setActiveEncyclopediaTab,
  setSearchQuery,
  setSearchValue,
  setTitle,
  setDateFrom,
  setDateTo,
  setAddress,
  setAuthorName,
  setStatus,
  setTotalCount,
  setSelectedArticle,
  setShowDetailView,
  setShowAddModal,
  setShowEditModal,
  setCheckedItems,
} from '../../store/encyclopediaTabSlice.js';
import {
  getHeaders,
  headerWidths,
  tabsConfig,
  buttonsConfig,
  leftSideConfig,
  rightSideConfig,
  statusFilterOptions,
  empty,
} from './encyclopediaTableConfig.js';
import { fetchArticles } from './encyclopediaTableService.js';
import Table from '../../components/list2/table';
import Pagination from '../../components/components/pagination/pagination.jsx';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import LineWithButtons from '../../components/components/line_with_buttons/lineWithButtons.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import ArticleDetailView from './articleDetailView.jsx';
import AddArticleModal from './addArticleModal.jsx';
import './Encyclopedia.css';

function Encyclopedia() {
  const dispatch = useDispatch();
  const activeEncyclopediaTab = useSelector((state) => state.encyclopedia.activeEncyclopediaTab);
  const totalPages = useSelector((state) => state.encyclopedia.maxPages);
  const currentPage = useSelector((state) => state.encyclopedia.currentPage);
  const sortBy = useSelector((state) => state.encyclopedia.sortBy);
  const sortDirection = useSelector((state) => state.encyclopedia.sortDirection);
  const checkedItems = useSelector((state) => state.encyclopedia.checkedItems);
  const searchQuery = useSelector((state) => state.encyclopedia.searchQuery);
  const searchValue = useSelector((state) => state.encyclopedia.searchValue);
  const title = useSelector((state) => state.encyclopedia.title);
  const dateFrom = useSelector((state) => state.encyclopedia.dateFrom);
  const dateTo = useSelector((state) => state.encyclopedia.dateTo);
  const address = useSelector((state) => state.encyclopedia.address);
  const authorName = useSelector((state) => state.encyclopedia.authorName);
  const status = useSelector((state) => state.encyclopedia.status);
  const totalCount = useSelector((state) => state.encyclopedia.totalCount);
  const showDetailView = useSelector((state) => state.encyclopedia.showDetailView);
  const showAddModal = useSelector((state) => state.encyclopedia.showAddModal);
  const showEditModal = useSelector((state) => state.encyclopedia.showEditModal);
  const selectedArticle = useSelector((state) => state.encyclopedia.selectedArticle);

  const [rows, setRows] = useState([]);
  const [articlesData, setArticlesData] = useState([]);
  const [articlesMap, setArticlesMap] = useState({}); // Store full article data by ID
  const [articlesList, setArticlesList] = useState([]); // Store full articles list for sidebar
  const sortArrowColorActive = '#FFFFFF';
  const sortArrowColorInactive = 'rgba(255,255,255,0.45)';

  // Handlers for row actions - MUST be defined before useEffect that uses them
  const handleAcceptArticle = useCallback((articleId) => {
    // Open detail view instead of accepting immediately
    dispatch(setSelectedArticle({ id: articleId }));
    dispatch(setShowDetailView(true));
  }, [dispatch]);

  const handleEditArticle = useCallback((articleId) => {
    dispatch(setSelectedArticle({ id: articleId }));
    dispatch(setShowEditModal(true));
  }, [dispatch]);

  const handleDeleteArticle = useCallback(async (articleId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten artykuł? Ta operacja jest nieodwracalna.')) {
      return;
    }
    try {
      const ApiService = (await import('../../components/api_services/apiService.jsx')).default;
      await ApiService.deleteArticle(articleId);
      alert('Artykuł został usunięty.');
      // Reload will happen automatically via useEffect
      window.location.reload();
    } catch (error) {
      console.error('Błąd podczas usuwania:', error);
      alert('Nie udało się usunąć artykułu.');
    }
  }, []);

  useEffect(() => {
    fetchArticles(
      sortBy,
      sortDirection,
      currentPage,
      dispatch,
      setMaxPages,
      setRows,
      setTotalCount,
      setArticlesData,
      checkedItems,
      searchQuery,
      title,
      dateFrom,
      dateTo,
      address,
      authorName,
      null, // userId
      null, // pinUrl
      status,
      setArticlesMap, // Pass setter for articles map
      setArticlesList, // Pass setter for articles list (for sidebar)
      handleAcceptArticle, // Pass accept handler
      handleEditArticle, // Pass edit handler
      handleDeleteArticle // Pass delete handler
    );
  }, [
    currentPage,
    sortBy,
    sortDirection,
    checkedItems,
    searchQuery,
    title,
    dateFrom,
    dateTo,
    address,
    authorName,
    status,
    handleAcceptArticle,
    handleEditArticle,
    handleDeleteArticle,
  ]);

  const headers = getHeaders(
    sortBy,
    sortDirection,
    dispatch,
    setSorting,
    sortArrowColorActive,
    sortArrowColorInactive,
    rows,
    checkedItems,
    articlesData,
    setCheckedItems
  );

  const paginationProps = {
    reduxSelector: (state) => state.encyclopedia.currentPage,
    reduxAction: setPage,
    totalPages: totalPages,
    maxVisibleButtons: 5,
  };

  const rightSideConfig2 = [
    {
      component: Pagination,
      props: paginationProps,
    },
  ];

  // Create fetch params for delete action
  const fetchParams = [
    sortBy,
    sortDirection,
    currentPage,
    dispatch,
    setMaxPages,
    setRows,
    setTotalCount,
    setArticlesData,
    checkedItems,
    searchQuery,
    title,
    dateFrom,
    dateTo,
    address,
    authorName,
    null, // userId
    null, // pinUrl
    status,
    setArticlesMap,
    setArticlesList,
    handleAcceptArticle,
    handleEditArticle,
    handleDeleteArticle,
  ];

  const handleToggleAddModal = () => {
    dispatch(setShowAddModal(!showAddModal));
  };

  const handleRowClick = (rowIndex) => {
    // Get article ID from row data (second column)
    const articleId = rows[rowIndex]?.[1];
    if (articleId) {
      dispatch(setSelectedArticle({ id: articleId }));
      dispatch(setShowDetailView(true));
    }
  };

  return (
    <div className="encyclopedia-container">
      {(showAddModal || showEditModal) && <AddArticleModal />}
      {showDetailView && (
        <ArticleDetailView 
          articlesList={articlesList}
          currentIndex={articlesList.findIndex(a => a.id === selectedArticle?.id)}
        />
      )}
      <Menu />
      <Content>
        <LineWithButtons
          tabs={tabsConfig(activeEncyclopediaTab, dispatch, setActiveEncyclopediaTab)}
          buttons={buttonsConfig(handleToggleAddModal)}
          rightSide={rightSideConfig(searchValue, dispatch)}
        />
        <div className="encyclopedia-info">
          <span className="encyclopedia-count">Wyszukano: {totalCount} wyników</span>
        </div>
        <div className="encyclopedia-filters">
          <div className="filter-group">
            <input
              className="filter-input"
              type="text"
              placeholder="Nazwa miejsca"
              value={title || ''}
              onChange={(e) => {
                dispatch(setTitle(e.target.value || null));
              }}
            />
          </div>
          <div className="filter-group">
            <input
              className="filter-input"
              type="date"
              placeholder="Data od"
              value={dateFrom || ''}
              onChange={(e) => {
                dispatch(setDateFrom(e.target.value || null));
              }}
            />
          </div>
          <div className="filter-group">
            <input
              className="filter-input"
              type="text"
              placeholder="Adres"
              value={address || ''}
              onChange={(e) => {
                dispatch(setAddress(e.target.value || null));
              }}
            />
          </div>
          <div className="filter-group">
            <input
              className="filter-input"
              type="text"
              placeholder="Autor"
              value={authorName || ''}
              onChange={(e) => {
                dispatch(setAuthorName(e.target.value || null));
              }}
            />
          </div>
          <div className="filter-group">
            <select
              className="filter-select"
              value={status || ''}
              onChange={(e) => {
                dispatch(setStatus(e.target.value || null));
              }}
            >
              {statusFilterOptions.map((option) => (
                <option key={option.value || 'empty'} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="table-container">
          <Table
            headers={headers}
            rows={rows}
            columnWidths={headerWidths}
            idViewing={false}
            idArrayId={1}
            cellClass2="cell-center"
            cellClass2ConditionList={[5, 6, 7, 8]}
          />
          <UniversalLineWithComponents
            leftSide={leftSideConfig(checkedItems, dispatch, fetchArticles, ...fetchParams)}
            rightSide={rightSideConfig2}
          />
        </div>
      </Content>
    </div>
  );
}

export default Encyclopedia;

